import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import type { ChgPracticeWindow, Payload } from '../../../../shared/riskregister/types';

export const RISK_REGISTER_QUERY_KEY = ['risk-register', 'payload'] as const;

function payloadUrl(asOf: string | undefined, chgWindow: ChgPracticeWindow, refresh = false): string {
  const params = new URLSearchParams();
  if (asOf) params.set('asOf', asOf);
  params.set('chgWindow', String(chgWindow));
  if (refresh) params.set('refresh', '1');
  const search = params.toString();
  return `/api/riskregister/payload${search ? `?${search}` : ''}`;
}

export function useRiskRegister(asOf: string | undefined, chgWindow: ChgPracticeWindow) {
  const getToken = useAccessToken();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [refreshError, setRefreshError] = React.useState<Error | null>(null);
  const queryKey = React.useMemo(
    () => [...RISK_REGISTER_QUERY_KEY, asOf || 'today', chgWindow] as const,
    [asOf, chgWindow],
  );

  const query = useQuery<Payload>({
    queryKey,
    queryFn: () => fetchApi<Payload>(payloadUrl(asOf, chgWindow), getToken),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const refresh = React.useCallback(async () => {
    setIsRefreshing(true);
    setRefreshError(null);
    try {
      await queryClient.cancelQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
      const payload = await fetchApi<Payload>(payloadUrl(asOf, chgWindow, true), getToken);
      queryClient.setQueryData(queryKey, payload);
    } catch (error) {
      setRefreshError(error instanceof Error ? error : new Error('Unable to refresh the Risk Register.'));
    } finally {
      setIsRefreshing(false);
    }
  }, [asOf, chgWindow, getToken, queryClient, queryKey]);

  return { ...query, refresh, isRefreshing, refreshError };
}
